import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { ArrowLeft, Calendar } from "lucide-react";
import { getService, getServices } from "@/api/services";
import { getAnnouncements } from "@/api/announcements";
import { SEO } from "@/components/site/SEO";

export function ServiceDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { lang } = useLang();
  const [s, setS] = useState<any>(null);
  const [allServices, setAllServices] = useState<any[]>([]);
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    Promise.all([
      getService(slug),
      getServices(true),
      getAnnouncements(true)
    ])
      .then(([serviceData, servicesData, announcementsData]) => {
        setS(serviceData);
        setAllServices(servicesData);
        setAnnouncements(announcementsData.slice(0, 5));
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load data:', err);
        setLoading(false);
      });
  }, [slug]);

  if (loading) return <div className="container mx-auto px-4 py-16 min-h-[50vh]"></div>;
  if (!s) return <div className="container mx-auto px-4 py-16 text-center">Hizmet bulunamadı. <Link to="/services" className="text-primary underline">Geri</Link></div>;

  const title = lang === "tr" ? s.title_tr : s.title_en;
  const short = lang === "tr" ? s.short_tr : s.short_en;
  const content = lang === "tr" ? s.content_tr : s.content_en;

  return (
    <div className="container mx-auto px-4 py-12">
      <SEO
        title={title}
        description={short}
        image={s.image_url}
        url={`/services/${slug}`}
        type="article"
      />
      <div className="flex flex-col-reverse lg:flex-row gap-10">
        {/* Sidebar */}
        <aside className="w-full lg:w-1/3 xl:w-1/4 flex-shrink-0">
          <div className="sticky top-28 space-y-6">
            {/* Services */}
            <div className="bg-white shadow-sm border p-5">
              <h3 className="font-bold text-lg text-primary mb-4 pb-2 border-b">
                {lang === "tr" ? "Hizmetlerimiz" : "Our Services"}
              </h3>
              <ul className="space-y-2">
                {allServices.map((item) => {
                  const itemTitle = lang === "tr" ? item.title_tr : item.title_en;
                  const isActive = item.slug === slug;
                  return (
                    <li key={item.id}>
                      <Link
                        to={`/services/${item.slug}`}
                        className={`block px-3 py-2 rounded-lg transition-colors text-sm ${
                          isActive
                            ? "bg-primary text-white font-semibold shadow-sm"
                            : "text-muted-foreground hover:bg-primary/5 hover:text-primary"
                        }`}
                      >
                        {itemTitle}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Announcements */}
            {announcements.length > 0 && (
              <div className="bg-white shadow-sm border p-5">
                <h3 className="font-bold text-lg text-primary mb-4 pb-2 border-b">
                  {lang === "tr" ? "Duyurular" : "Announcements"}
                </h3>
                <ul className="space-y-3">
                  {announcements.map((item) => {
                    const itemTitle = lang === "tr" ? item.title_tr : item.title_en;
                    const date = new Date(item.created_at).toLocaleDateString(lang === "tr" ? "tr-TR" : "en-US", {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    });
                    return (
                      <li key={item.id}>
                        <Link
                          to={`/announcements/${item.id}`}
                          className="block p-3 rounded-lg hover:bg-primary/5 transition-colors group"
                        >
                          <h4 className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-1">
                            {itemTitle}
                          </h4>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Calendar size={12} />
                            <span>{date}</span>
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
                <Link
                  to="/announcements"
                  className="block mt-4 pt-3 border-t text-center text-sm text-primary font-medium hover:text-primary-dark transition-colors"
                >
                  {lang === "tr" ? "Tüm Duyurular →" : "All Announcements →"}
                </Link>
              </div>
            )}
          </div>
        </aside>

        {/* Content */}
        <article className="w-full lg:w-2/3 xl:w-3/4">
          <Link to="/services" className="inline-flex lg:hidden items-center gap-2 text-primary mb-6 hover:opacity-80 transition-opacity">
            <ArrowLeft size={16}/> {lang === "tr" ? "Tüm Hizmetler" : "All Services"}
          </Link>
          {s.image_url && <img src={s.image_url} alt={title} className="w-full h-auto max-h-[400px] object-cover mb-6 shadow-sm" />}
          <div className="bg-white shadow-sm border p-6">
            <h1 className="text-4xl font-bold text-primary mb-4">{title}</h1>
            <p className="text-lg text-muted-foreground mb-6 font-medium">{short}</p>
            <div 
              className="prose max-w-none text-foreground leading-relaxed prose-p:mb-4 prose-headings:text-primary prose-a:text-blue-600 hover:prose-a:text-blue-800"
              dangerouslySetInnerHTML={{ __html: content }}
            />
          </div>
        </article>
      </div>
    </div>
  );
}
